import {useCookieStorage} from '@github-ui/use-safe-storage/cookie-storage'
import {useCallback} from 'react'

/**
 * Site-wide cookie key for sidebar collapse state. Sharing one key lets a
 * user's preference carry across surfaces; pass a custom `storageKey` only when
 * a surface needs independent state.
 */
export const GLOBAL_SIDEBAR_STORAGE_KEY = 'primer_sidebar_expanded'

export interface UseCollapsibleSidebarStateOptions {
  /**
   * The storage key used to persist the expanded/collapsed state. Defaults to
   * {@link GLOBAL_SIDEBAR_STORAGE_KEY} so most of the site shares one cookie;
   * pass a custom key only when a surface needs independent collapse state.
   */
  storageKey?: string
  defaultExpanded?: boolean
  /**
   * Server-read seed so SSR markup matches the client's first paint (prevents
   * the flash on hard navigation). Falls back to `defaultExpanded`.
   */
  initialExpanded?: boolean
}

export interface CollapsibleSidebarState {
  isExpanded: boolean
  toggle: () => void
  setExpanded: (expanded: boolean | ((prev: boolean) => boolean)) => void
}

/**
 * Cookie-backed, SSR-safe expanded/collapsed state. Seed the first render with
 * `initialExpanded` (the server-read cookie value) to avoid a flash on hard
 * navigation.
 */
export function useCollapsibleSidebarState({
  storageKey = GLOBAL_SIDEBAR_STORAGE_KEY,
  defaultExpanded = true,
  initialExpanded,
}: UseCollapsibleSidebarStateOptions = {}): CollapsibleSidebarState {
  const seed = initialExpanded ?? defaultExpanded
  const [isExpanded, setIsExpanded] = useCookieStorage<boolean>(storageKey, seed)

  const setExpanded = useCallback(
    (expanded: boolean | ((prev: boolean) => boolean)) => {
      setIsExpanded(expanded)
    },
    [setIsExpanded],
  )

  const toggle = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [setIsExpanded])

  return {isExpanded, toggle, setExpanded}
}
